import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';

import TrunksIp from './components/TrunksIp';
import TrunksAuth from './components/TrunksAuth';
import TrunkOptionCards from './components/TrunkOptionCards';
import TrunksInboundDispatcher from './TrunksInboundDispatcher';

import { accessLocalStorage, exportCreateTrunks, exportCreateTrunksBatch, exportPreviewTrunksBatch, valuesAnalyze, changeViewForms, readAllTenant, sendReloadAsteriskSystem, createInboundDispatcherTrunk, getCarriers } from '../../../services';
import { scopeByTenant } from '../../../services/tenantScope';
import { TRUNK_NAME_MESSAGES, buildTrunkName, trunkNamesOf, findTenantByOptionValue } from '../../../services/trunkName';

import '../../../libs/bulma.min.css';
import './TrunksForm.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const incrementNumericStringForPreview = (numStr, delta) => {
  if (!numStr || !/^[0-9]+$/.test(numStr)) return null;
  const width = numStr.length;
  try {
    const incremented = (BigInt(numStr) + BigInt(delta)).toString();
    return incremented.length <= width ? incremented.padStart(width, '0') : incremented;
  } catch (error) {
    return null;
  }
};

const computeBatchRange = (trunkNumber, qtt) => {
  const parsedQtt = Number(qtt);
  if (!Number.isInteger(parsedQtt) || parsedQtt < 1) return null;
  const finalNumber = incrementNumericStringForPreview(trunkNumber, parsedQtt - 1);
  if (finalNumber === null) return null;
  return { inicial: trunkNumber, final: finalNumber };
};

const CARRIERS_CATALOG_TENANT = 'vgs';

const NO_TRUNK_NAME = { ok: false };

const MISSING_IDENTITY_MESSAGE = 'Selecione operadora e cliente.';

const CREATION_MODE_OPTIONS = [
  { id: 'creation-mode-unico', value: 'unico', title: 'Tronco/DDR Único', description: 'Cria um único tronco.' },
  { id: 'creation-mode-faixa', value: 'faixa', title: 'Faixa de Tronco/DDR', description: 'Cria vários troncos unidirecionais em sequência.' },
];

const TRUNK_TYPE_OPTIONS = [
  { id: 'type-auth', value: 'Auth', title: 'Autenticado', description: 'Registro na operadora com usuário e senha.' },
  { id: 'type-ip', value: 'IP', title: 'IP', description: 'Operadora identificada pelo endereço IP.' },
];

const INBOUND_DISPATCHER_OPTION = {
  id: 'type-inboundd',
  value: 'InboundDispatcher',
  title: 'Inbound Dispatcher (VGS)',
  description: 'Distribui chamadas de entrada por IP de origem.',
};

const carriersStateFrom = (result) => {
  if (!result || !result.success) return { status: 'error', carriers: [] };
  const carriers = Array.isArray(result.data) ? result.data : [];
  return { status: carriers.length === 0 ? 'empty' : 'ready', carriers };
};

function TrunksCreate() {
  const history = useHistory();

  const [values, setValues] = useState({
    "ipLocal": "",
    "trunkNumber": "",
    "authUsername": "",
    "password": "",
    "tenant": "",
    "realNumber": "",
    "mode": "",
    "qualifyFrequency": 120,
    "regExpiration": 3600,
    "sendRegister": true,
    "qtt": "",
  });
  const [validate, setValidate] = useState({});
  const [disable, setDisable] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [creationMode, setCreationMode] = useState('unico');
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isVgsMaster, setIsVgsMaster] = useState(false);
  const [inboundState, setInboundState] = useState({ valid: false });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [carriersState, setCarriersState] = useState({ status: 'loading', carriers: [] });

  const isBatchMode = creationMode === 'faixa';
  const batchRange = isBatchMode ? computeBatchRange(values.trunkNumber, values.qtt) : null;
  const batchLastNumber = batchRange ? batchRange.final : null;

  const trunkNameResult = useMemo(() => {
    if (carriersState.status !== 'ready') return NO_TRUNK_NAME;
    const selectedCarrier = carriersState.carriers.find((carrier) => carrier.carrierId === Number(values.carrierId));
    const selectedTenant = findTenantByOptionValue(tenants, values.tenant);
    if (!selectedCarrier || !selectedTenant) return NO_TRUNK_NAME;
    return buildTrunkName({
      carrierName: selectedCarrier.name,
      tenant: selectedTenant,
      existingNames: trunkNamesOf(selectedTenant.endpoints),
      batchLastNumber,
      batch: isBatchMode,
    });
  }, [carriersState, tenants, values.carrierId, values.tenant, isBatchMode, batchLastNumber]);

  const identity = {
    ...carriersState,
    trunkName: trunkNameResult.ok ? trunkNameResult.value : '',
    nameError: trunkNameResult.code ? TRUNK_NAME_MESSAGES[trunkNameResult.code] : '',
    batchBase: isBatchMode && trunkNameResult.ok ? trunkNameResult.value : '',
  };

  const trunkNameBlockMessage = () => TRUNK_NAME_MESSAGES[trunkNameResult.code] || MISSING_IDENTITY_MESSAGE;

  const trunkPayload = (extra = {}) => {
    const { carrierId, ...formValues } = values;
    return {
      ...formValues,
      provider: trunkNameResult.value,
      codec: Array.isArray(values.codec) ? values.codec.join(',') : values.codec,
      ...extra,
    };
  };

  const { changeBorderImputToRedOrNormal, changeVisibityMessages} = changeViewForms;

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    const { user: { role, idtenant } } = dataUser;
    if (!dataUser) return history.push('/');
    let fetchTenants = await readAllTenant();
    if (!fetchTenants) return;
    fetchTenants = scopeByTenant(fetchTenants, (tenant) => tenant.short_name, idtenant);
    setTenants(fetchTenants);
    setIsVgsMaster(idtenant === 'vgs' && role === 'root');
  }, []);

  const changeScreen = (check, name) => {
    changeBorderImputToRedOrNormal(check, name);
    changeVisibityMessages(check, `${name}-invalid-data`);
  };

  const handleChangeDataAnalyze = (name, value) => {
    const { checkNumber, isTripleChar } = valuesAnalyze;

    if(name === 'trunkNumber') {
      const checkValueIsNumber = checkNumber(value);
      changeScreen(checkValueIsNumber, name);
      return checkValueIsNumber;
    };

    if(name === 'password') {
      const passwordIsValid = value.length > 5 ? true : false;
      changeScreen(passwordIsValid, name);
      return passwordIsValid;
    }

    if(name === 'mode') {
      const modeAnalyze = value === 'bidirecional' || value === 'unidirecional' ? true : false;

      const realNumberFieldIP = document.getElementById('realNumber-field');
      const realNumberFieldAuth = document.getElementById('realNumber-field-auth');

      if (value === 'unidirecional') {
        if (realNumberFieldIP) realNumberFieldIP.style.display = 'flex';
        if (realNumberFieldAuth) realNumberFieldAuth.style.display = 'flex';
      } else {
        if (realNumberFieldIP) realNumberFieldIP.style.display = 'none';
        if (realNumberFieldAuth) realNumberFieldAuth.style.display = 'none';
        setValues((prevState) => ({...prevState, realNumber: ''}));
      }

      return modeAnalyze;
    }

    if(name === 'realNumber') {
      const realNumberIsValid = value.length >= 10 ? true : false;
      changeScreen(realNumberIsValid, name);
      return realNumberIsValid;
    }

    if(name === 'qualifyFrequency') {
      const parsed = Number(value);
      const qualifyFrequencyIsValid = Number.isInteger(parsed) && parsed >= 0 && parsed <= 86400;
      changeScreen(qualifyFrequencyIsValid, name);
      return qualifyFrequencyIsValid;
    }

    if(name === 'sendRegister') {
      if (value === false) {
        validate.regExpiration = true;
        changeScreen(true, 'regExpiration');
      }
      return true;
    }

    if(name === 'regExpiration') {
      if (values.sendRegister === false) return true;
      const parsed = Number(value);
      const regExpirationIsValid = Number.isInteger(parsed) && parsed >= 60 && parsed <= 7200;
      changeScreen(regExpirationIsValid, name);
      return regExpirationIsValid;
    }

    if(name === 'qtt') {
      const parsed = Number(value);
      const qttIsValid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 500;
      changeScreen(qttIsValid, name);
      return qttIsValid;
    }

    if(name === 'authUsername') {
      const checkValueIsCharacters = isTripleChar(value);
      changeScreen(checkValueIsCharacters, name);
      return checkValueIsCharacters;
    }

    if(name === 'ipSbcTrunk'|| name === 'ipLocal') {
      const checkValueIsCharacters = true;
      return checkValueIsCharacters;
    }
  };

  const handleChangeCodec = (name, value) => {
    let prevSetValueCodec = values[name];

    if (!values.hasOwnProperty('codec')) prevSetValueCodec = [];
    prevSetValueCodec = prevSetValueCodec.includes(value) ? prevSetValueCodec.filter((codec) => codec !== value) : [...prevSetValueCodec, value];

    setValues((prevState) => ({ ...prevState, [name]: prevSetValueCodec }));
  };

  const handleChangeValues = (item) => {
    const { name, value, type, checked } = item;
    const resolvedValue = type === 'checkbox' && name === 'sendRegister' ? checked : value;

    if (name === '__inboundDispatcherState') {
      setInboundState(value);
      setDisable(!value.valid);
      return;
    }

    const itemStatus = handleChangeDataAnalyze(name, resolvedValue);
    if (name === 'codec') handleChangeCodec(name, resolvedValue);
    setValidate((prevState) => prevState, validate[name] = itemStatus === undefined  ? true : itemStatus);
    if (name !== 'codec') setValues((prevState) => ({ ...prevState, [name]: resolvedValue }));
    if(Object.keys(validate).length >= 8) Object.values(validate).includes(false) ? setDisable(true) : setDisable(false);
    return;
  };

  const typeTrunkSelected = (item) => {
    const { value } = item;
    setSelectedType(value);
    if (value === 'IP') {
      changeVisibityMessages(true, 'trunk-auth-div');
      changeVisibityMessages(true, 'trunk-inboundd-div');
      changeVisibityMessages(false, 'trunk-ip-div');
      changeVisibityMessages(false, 'button-send-form');
      handleChangeValues(item);
      return;
    }
    if (value === 'InboundDispatcher') {
      changeVisibityMessages(true, 'trunk-ip-div');
      changeVisibityMessages(true, 'trunk-auth-div');
      changeVisibityMessages(false, 'trunk-inboundd-div');
      changeVisibityMessages(false, 'button-send-form');
      handleChangeValues(item);
      return;
    }

    changeVisibityMessages(true, 'trunk-ip-div');
    changeVisibityMessages(true, 'trunk-inboundd-div');
    changeVisibityMessages(false, 'trunk-auth-div');
    changeVisibityMessages(false, 'button-send-form');
    handleChangeValues(item);
    return;
  };

  const selectCreationMode = (mode) => {
    setCreationMode(mode);
    setPreviewResult(null);
    if (mode === 'faixa') {
      if (selectedType === 'InboundDispatcher') {
        setSelectedType('');
        changeVisibityMessages(true, 'trunk-ip-div');
        changeVisibityMessages(true, 'trunk-auth-div');
        changeVisibityMessages(true, 'trunk-inboundd-div');
        changeVisibityMessages(false, 'button-send-form');
      }
      handleChangeValues({ name: 'mode', value: 'unidirecional' });
    }
  };

  const finishWithSuccess = (message = 'Tronco criado com sucesso!') => {
    setCreated(true);
    setAlert({ show: true, type: 'success', message });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => history.push('/config/tronco/lista'), 3000);
  };

  const showCreateError = (message) => {
    setAlert({ show: true, type: 'danger', message });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sendInboundDispatcherTrunk = async () => {
    if (!inboundState.valid) {
      showCreateError('Preencha corretamente os dados do Inbound Dispatcher antes de criar o tronco.');
      return;
    }

    const response = await createInboundDispatcherTrunk({
      provider: inboundState.provider,
      ipsMatch: inboundState.ipsMatch,
      ipLocal: inboundState.ipLocal,
      codec: inboundState.codec,
    });

    if (response.status !== 201) {
      const responseBody = response.data || {};
      showCreateError(responseBody.message || responseBody.error || 'Não foi possível criar o tronco Inbound Dispatcher.');
      return;
    }

    finishWithSuccess();
  };

  const handlePreviewBatch = async () => {
    if (!trunkNameResult.ok) {
      setPreviewResult({ error: trunkNameBlockMessage() });
      return;
    }
    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const result = await exportPreviewTrunksBatch(trunkPayload({ qtt: Number(values.qtt) }));

      if (!result.success) {
        setPreviewResult({ error: result.error });
        return;
      }

      setPreviewResult({ data: result.data });
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendBatchTrunkGenerate = async () => {
    const result = await exportCreateTrunksBatch(trunkPayload({ qtt: Number(values.qtt) }));

    if (!result.success) {
      showCreateError(result.error);
      return;
    }

    const { criados = 0, solicitados = 0, pulados = [], reload } = result.data || {};
    const hasSkipped = Array.isArray(pulados) && pulados.length > 0;
    const reloadFailed = reload === 'falha';

    let message = `Faixa criada: ${criados}/${solicitados} troncos criados.`;
    if (hasSkipped) message += ` ${pulados.length} número(s) já existiam e foram pulados.`;
    if (reloadFailed) message += ' O reload do Asterisk falhou; os troncos foram criados normalmente, repita o reload quando possível.';

    if (hasSkipped || reloadFailed) {
      setAlert({ show: true, type: 'success', message });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    finishWithSuccess(message);
  };

  const sendDataTrunkGenerate = async () => {
    setSaving(true);
    setAlert({ show: false, type: '', message: '' });

    try {
      if (selectedType === 'InboundDispatcher') {
        await sendInboundDispatcherTrunk();
        return;
      }

      if (!trunkNameResult.ok) {
        showCreateError(trunkNameBlockMessage());
        return;
      }

      if (isBatchMode) {
        await sendBatchTrunkGenerate();
        return;
      }

      const result = await exportCreateTrunks(trunkPayload());

      if (!result.success) {
        showCreateError(result.error);
        return;
      }

      await sendReloadAsteriskSystem({ command: 'asterisk' }).catch(() => null);
      finishWithSuccess();
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  useEffect(() => {
    let active = true;
    getCarriers(CARRIERS_CATALOG_TENANT).then((result) => {
      if (active) setCarriersState(carriersStateFrom(result));
    });
    return () => {
      active = false;
    };
  }, []);

  const trunkTypeOptions = isVgsMaster && !isBatchMode ? [...TRUNK_TYPE_OPTIONS, INBOUND_DISPATCHER_OPTION] : TRUNK_TYPE_OPTIONS;
  const needsGeneratedName = selectedType !== 'InboundDispatcher';

  return (
    <div className="trunks-form-container">
      <div className="trunks-form-header">
        <div className="trunks-form-title-wrapper">
          <svg className="trunks-form-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <div>
            <h1 className="trunks-form-title">Adicionar Tronco</h1>
            <p className="trunks-form-subtitle">Configure um novo tronco SIP (IP ou Autenticado)</p>
          </div>
        </div>
        <Link to="/config/tronco/lista" className="btn-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          Voltar
        </Link>
      </div>

      {alert.show && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : 'Erro!'}</span>
              <button className="alert-close" aria-label="delete" onClick={() => setAlert({ show: false, type: '', message: '' })}>×</button>
            </div>
            <div className="alert-body">
              {alert.message}
            </div>
          </div>
        </div>
      )}

      {!created && (
        <>
          <div className="trunk-type-card">
            <p className="trunk-type-title">Modo de Criação</p>
            <TrunkOptionCards
              name="creationMode"
              options={CREATION_MODE_OPTIONS}
              selectedValue={creationMode}
              onSelect={(target) => selectCreationMode(target.value)}
            />
          </div>

          <div id="trunk-all-form" className="trunk-type-card">
            <p className="trunk-type-title">Tipo de Tronco</p>
            <TrunkOptionCards name="type" options={trunkTypeOptions} selectedValue={selectedType} onSelect={typeTrunkSelected} />
            <p name="type-invalid-data" className="form-error is-hidden">Selecione o tipo de tronco!</p>
          </div>

          <div name="trunk-ip-div" className="is-hidden">
            <TrunksIp changeValues={handleChangeValues} tenants={tenants} values={values} identity={identity} batchMode={isBatchMode} />
          </div>

          <div name="trunk-auth-div" className="is-hidden">
            <TrunksAuth changeValues={handleChangeValues} tenants={tenants} values={values} identity={identity} batchMode={isBatchMode} />
          </div>

          {isVgsMaster && !isBatchMode && (
            <div name="trunk-inboundd-div" className="is-hidden">
              <div className="form-card">
                <TrunksInboundDispatcher changeValues={handleChangeValues} />
              </div>
            </div>
          )}

          {isBatchMode && (
            <div className="form-card">
              <p className="trunk-type-title">Pré-visualização da Faixa</p>
              <p className="form-help">
                {batchRange
                  ? `Faixa resultante: ${batchRange.inicial} até ${batchRange.final}`
                  : 'Informe Tronco Inicial e Quantidade para ver a faixa resultante.'}
              </p>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  disabled={previewLoading || !batchRange}
                  onClick={handlePreviewBatch}
                >
                  {previewLoading ? 'Verificando...' : 'Pré-visualizar'}
                </button>
              </div>
              {previewResult && previewResult.error && (
                <p className="form-error">{previewResult.error}</p>
              )}
              {previewResult && previewResult.data && (
                <div>
                  <p className="form-help">
                    {previewResult.data.disponiveis} de {previewResult.data.solicitados} número(s) disponíveis.
                  </p>
                  {Array.isArray(previewResult.data.pulados) && previewResult.data.pulados.length > 0 && (
                    <ul>
                      {previewResult.data.pulados.map((item) => (
                        <li key={item.numero}>{item.numero} ({item.provider}) — {item.motivo}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          <div name="button-send-form" className="is-hidden">
            <div className="form-actions trunk-form-actions-end">
              <Link to="/config/tronco/lista" className="btn-cancel">
                <FontAwesomeIcon icon={faArrowLeft} />
                Cancelar
              </Link>
              <button
                className="btn-submit"
                type="submit"
                disabled={disable || saving || (needsGeneratedName && !trunkNameResult.ok) || (isBatchMode && !(Number.isInteger(Number(values.qtt)) && Number(values.qtt) >= 1 && Number(values.qtt) <= 500))}
                onClick={() => sendDataTrunkGenerate()}
              >
                <FontAwesomeIcon icon={faSave} />
                <span>{saving ? 'Criando...' : (isBatchMode ? 'Criar Faixa de Troncos' : 'Criar Tronco')}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TrunksCreate;
